import { Box, Divider, Typography } from '@mui/material'
import { parseISO } from 'date-fns'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Accordion from '../../../../components/shared/accordion/accordion.component'
import Button from '../../button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  BUTTON_VARIANTS,
} from '../../../../components/shared/button/button.constants'
import { isLastElement } from '@hapana/shared/utils/array.util'
import { formatDistanceToNowAbbreviated } from '@hapana/shared/utils/date.util'

import { MAXIMUM_CREDIT_TOTAL } from '../individual-alerts.constants'
import AlertSection from '../alert-section/alert-section.component'

import { styles } from './packages-individual-alert.styles'

const PackagesIndividualAlert = ({ packages }) => {
  const { t } = useTranslation(['common', 'checkIn'])
  const hasMultiplePackages = packages.length > 1
  const { id, lastUsedOn, packageDefinition, site, creditsTotal, creditsConsumed } = packages[0]
  const managePackagesUrl =
    packages[0].siteId && packages[0].clientId
      ? `/s/${packages[0].siteId}/clients/${packages[0].clientId}?tab=packages`
      : null
  const [isOpen, setIsOpen] = useState(false)

  const handleChange = (value) => {
    setIsOpen(value)
  }

  const getCreditsDisplay = (pkg) => {
    if (pkg.creditsTotal >= MAXIMUM_CREDIT_TOTAL) {
      return t('checkIn:unlimited')
    }
    return t('checkIn:creditsLeft', {
      count: pkg.creditsTotal - pkg.creditsConsumed,
    })
  }

  const renderPackageInfo = (pkg) => (
    <Box sx={styles.flexRow}>
      <Typography typography="medium" color="gray.64">
        {[
          getCreditsDisplay(pkg),
          pkg.lastUsedOn
            ? `${t('common:used')} ${formatDistanceToNowAbbreviated(parseISO(pkg.lastUsedOn))}`
            : null,
          pkg.site?.name,
        ]
          .filter(Boolean)
          .join(' • ')}
      </Typography>
    </Box>
  )

  const renderSinglePackage = () => {
    return (
      <Accordion
        isOpen={isOpen}
        onChange={handleChange}
        key={id}
        summaryStyles={{
          ...styles.accordionSummary,
          ...(isOpen && styles.openedSummary),
        }}
        containerStyles={styles.accordionContent}
        content={
          <>
            <Box sx={styles.buttonWrapper}>
              <Button
                variant={BUTTON_VARIANTS.TEXT}
                size={BUTTON_SIZE.SMALL}
                theme={BUTTON_THEME.PRIMARY}
                stroke
                label={t('checkIn:viewPaymentDetails')}
                href={`/s/${packages[0].siteId}/retail/recurring-payment/${id}`}
              />
            </Box>
            <Box sx={styles.buttonWrapper}>
              <Button
                variant={BUTTON_VARIANTS.TEXT}
                size={BUTTON_SIZE.SMALL}
                theme={BUTTON_THEME.PRIMARY}
                stroke
                label={t('checkIn:managePackages')}
                href={managePackagesUrl}
              />
            </Box>
          </>
        }
        groupedRows
        label={
          <>
            {isOpen && (
              <>
                <Box sx={styles.flexRow}>
                  <Typography typography="header4" color="gray.64">
                    {packageDefinition.name}
                  </Typography>
                </Box>
                <Box sx={styles.flexRow}>
                  <Typography typography="medium" color="gray.64">
                    {[
                      getCreditsDisplay(packages[0]),
                      lastUsedOn
                        ? `${t('common:used')} ${formatDistanceToNowAbbreviated(parseISO(lastUsedOn))}`
                        : null,
                      site?.name,
                    ]
                      .filter(Boolean)
                      .join(' • ')}
                  </Typography>
                </Box>
              </>
            )}
            {!isOpen && (
              <Box sx={styles.flexRow}>
                <Typography typography="medium" color="gray.64">
                  {[packageDefinition.name, getCreditsDisplay(packages[0]), site?.name]
                    .filter(Boolean)
                    .join(' • ')}
                </Typography>
              </Box>
            )}
          </>
        }
      />
    )
  }

  return (
    <AlertSection
      type="packages"
      title={t('checkIn:packages')}
      count={
        hasMultiplePackages ? t('checkIn:totalPackagesCard', { count: packages.length }) : undefined
      }
    >
      {!hasMultiplePackages ? (
        renderSinglePackage()
      ) : (
        <Accordion
          containerStyles={styles.accordionContent}
          summaryStyles={{
            ...styles.accordionSummary,
            ...(isOpen && styles.openedSummary),
          }}
          isOpen={isOpen}
          onChange={handleChange}
          content={
            <>
              {packages.slice(1).map((singlePackage, index) => {
                const remainingPackages = packages.slice(1)
                return (
                  <Box
                    key={singlePackage.id}
                    sx={{
                      ...styles.multiplePaymentWrapper,
                      ...(isOpen && index === 0 && styles.openedMultiplePaymentWrapper),
                    }}
                  >
                    {index === 0 && <Divider sx={styles.internalDivider} />}
                    <Box sx={styles.flexRow}>
                      <Typography typography="header4" color="gray.80">
                        {singlePackage.packageDefinition.name}
                      </Typography>
                    </Box>
                    {renderPackageInfo(singlePackage)}
                    <Box sx={styles.buttonWrapper}>
                      <Button
                        variant={BUTTON_VARIANTS.TEXT}
                        size={BUTTON_SIZE.SMALL}
                        theme={BUTTON_THEME.PRIMARY}
                        stroke
                        label={t('checkIn:viewPaymentDetails')}
                        href={`/s/${singlePackage.siteId}/retail/recurring-payment/${singlePackage.id}`}
                      />
                    </Box>
                    {isLastElement(remainingPackages, index) && (
                      <Divider sx={styles.internalDivider} />
                    )}
                  </Box>
                )
              })}
              <Box sx={styles.buttonWrapper}>
                <Button
                  variant={BUTTON_VARIANTS.TEXT}
                  size={BUTTON_SIZE.SMALL}
                  theme={BUTTON_THEME.PRIMARY}
                  stroke
                  sx={styles.margin}
                  label={t('checkIn:managePackages')}
                  href={managePackagesUrl}
                />
              </Box>
            </>
          }
          groupedRows
          label={
            <>
              {isOpen && (
                <>
                  <Box sx={styles.flexRow}>
                    <Typography typography="header4" color="gray.80">
                      {packages[0].packageDefinition.name}
                    </Typography>
                  </Box>
                  {renderPackageInfo(packages[0])}
                  <Box sx={styles.buttonWrapper}>
                    <Button
                      variant={BUTTON_VARIANTS.TEXT}
                      size={BUTTON_SIZE.SMALL}
                      theme={BUTTON_THEME.PRIMARY}
                      stroke
                      label={t('checkIn:viewPaymentDetails')}
                      href={`/s/${packages[0].siteId}/retail/recurring-payment/${packages[0].id}`}
                    />
                  </Box>
                </>
              )}
              {!isOpen && (
                <Box sx={styles.flexRow}>
                  <Typography typography="medium" color="gray.64">
                    {packages.length > 2
                      ? `${packages
                          .slice(0, 2)
                          .map((p) => p.packageDefinition.name)
                          .join(', ')}...`
                      : packages.map((p) => p.packageDefinition.name).join(', ')}
                  </Typography>
                </Box>
              )}
            </>
          }
        />
      )}
    </AlertSection>
  )
}

PackagesIndividualAlert.propTypes = {
  packages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      siteId: PropTypes.string,
      clientId: PropTypes.string,
      packageDefinition: PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
      }),
      lastUsedOn: PropTypes.string,
      creditsConsumed: PropTypes.number,
      creditsTotal: PropTypes.number,
      site: PropTypes.shape({
        name: PropTypes.string,
      }),
    })
  ),
}

export default PackagesIndividualAlert
